import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Megaphone, Plus, Trash2, Edit3, Save, X, Power, Store, Globe, Calendar, MapPin } from 'lucide-react';
import { Card, Badge } from './AdminPanelPrimitives';
import { EmptyState } from './adminDesignSystem';
import { AdminAutocomplete } from './AdminAutocomplete';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPE_BADGE = {
  info: 'blue',
  promo: '',
  warn: 'orange',
  critical: 'red',
};

const TYPES = ['info', 'promo', 'warn', 'critical'];
const ROLES = ['', 'guest', 'host', 'venue_owner', 'admin', 'staff'];

// CTA target options. Admins pick one; the picker below resolves to a
// real URL. Internal venue/event mentions become deep-links that the
// announcement banner opens via react-router, so taps land on the venue
// card without a full page reload.
const CTA_TARGETS = [
  { id: 'none', label: 'No CTA' },
  { id: 'venue', label: 'Open a venue' },
  { id: 'route', label: 'In-app route' },
  { id: 'external', label: 'External URL' },
];

const emptyForm = {
  text: '',
  type: 'info',
  active: true,
  audience_role: '',
  dismissible: true,
  cta_label: '',
  cta_target: 'none',
  cta_url: '',
  cta_venue: null, // { id, name, address }
  starts_at: '',
  expires_at: '',
  schedule_city: null, // { city, country, timezone } — for tz-aware scheduling
};

// Convert datetime-local "YYYY-MM-DDTHH:mm" into an ISO UTC string,
// optionally treating the input as wall-clock time in the given IANA
// timezone. We use Intl to look up the offset for the picked moment
// (handles DST transitions correctly).
const toIsoInTimezone = (value, timezone) => {
  if (!value) return '';
  if (!timezone) return new Date(value).toISOString();
  // Treat the value as wall-clock in `timezone`. We build a naive Date in
  // the local browser zone and then subtract the offset difference.
  const [datePart, timePart = '00:00'] = value.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hour, minute] = timePart.split(':').map(Number);
  // Start by trusting that the naive UTC date represents the same wall clock
  // in the picked timezone — this gives us an initial guess. We then ask
  // Intl what the actual local time of that moment is in the target zone,
  // measure the drift in minutes, and shift back to nail the exact UTC.
  let utcGuess = Date.UTC(year, month - 1, day, hour, minute);
  for (let i = 0; i < 2; i += 1) {
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour12: false,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
    const parts = fmt.formatToParts(new Date(utcGuess)).reduce((acc, p) => {
      if (p.type !== 'literal') acc[p.type] = p.value;
      return acc;
    }, {});
    const localUtc = Date.UTC(
      Number(parts.year), Number(parts.month) - 1, Number(parts.day),
      Number(parts.hour === '24' ? 0 : parts.hour), Number(parts.minute), Number(parts.second),
    );
    const drift = localUtc - utcGuess;
    if (drift === 0) break;
    utcGuess -= drift;
  }
  return new Date(utcGuess).toISOString();
};

const guessUserTimezone = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; }
  catch { return 'UTC'; }
};

export const AdminAnnouncementsPanel = ({ headers }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const browserTimezone = useMemo(() => guessUserTimezone(), []);
  const effectiveTimezone = form.schedule_city?.timezone || browserTimezone;

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/announcements`, { headers });
      setItems(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load announcements');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const resetForm = () => { setShowForm(false); setEditingId(null); setForm(emptyForm); };

  const startEdit = (row) => {
    // Best-effort reverse map: if the CTA URL looks like /guest/venues?venue=ID,
    // mark it as a venue target so the picker pre-populates.
    let cta_target = 'none';
    let cta_venue = null;
    let cta_url = '';
    if (row.cta_url) {
      cta_url = row.cta_url;
      if (/^\/guest\/venues\?venue=/.test(row.cta_url)) {
        cta_target = 'venue';
        const venueId = (row.cta_url.match(/venue=([^&]+)/) || [])[1];
        if (venueId) cta_venue = { id: venueId, name: row.cta_venue_name || venueId };
      } else if (row.cta_url.startsWith('http')) {
        cta_target = 'external';
      } else {
        cta_target = 'route';
      }
    }
    setEditingId(row.id);
    setForm({
      text: row.text || '',
      type: row.type || 'info',
      active: !!row.active,
      audience_role: row.audience_role || '',
      dismissible: !!row.dismissible,
      cta_label: row.cta_label || '',
      cta_target,
      cta_url,
      cta_venue,
      starts_at: row.starts_at ? row.starts_at.slice(0, 16) : '',
      expires_at: row.expires_at ? row.expires_at.slice(0, 16) : '',
      schedule_city: row.schedule_city || null,
    });
    setShowForm(true);
  };

  const computedCtaUrl = useMemo(() => {
    if (form.cta_target === 'venue' && form.cta_venue?.id) {
      return `/guest/venues?venue=${form.cta_venue.id}`;
    }
    if (form.cta_target === 'route' || form.cta_target === 'external') {
      return form.cta_url.trim();
    }
    return '';
  }, [form.cta_target, form.cta_url, form.cta_venue]);

  const save = async () => {
    if (form.text.trim().length < 1) {
      toast.error('Text is required.');
      return;
    }
    if (form.cta_target !== 'none' && !form.cta_label.trim()) {
      toast.error('Give the CTA button a label (or pick "No CTA").');
      return;
    }
    if (form.cta_target === 'venue' && !form.cta_venue?.id) {
      toast.error('Pick a venue for the CTA.');
      return;
    }
    if ((form.cta_target === 'route' || form.cta_target === 'external') && !form.cta_url.trim()) {
      toast.error('Enter a URL for the CTA.');
      return;
    }
    const startsIso = toIsoInTimezone(form.starts_at, effectiveTimezone);
    const expiresIso = toIsoInTimezone(form.expires_at, effectiveTimezone);
    if (startsIso && expiresIso && startsIso >= expiresIso) {
      toast.error('"Expires at" must be after "Starts at".');
      return;
    }
    const payload = {
      text: form.text.trim(),
      type: form.type,
      active: form.active,
      audience_role: form.audience_role,
      dismissible: form.dismissible,
      cta_label: form.cta_target === 'none' ? '' : form.cta_label.trim(),
      cta_url: computedCtaUrl,
      starts_at: startsIso,
      expires_at: expiresIso,
    };
    try {
      if (editingId) {
        await axios.patch(`${API}/admin/announcements/${editingId}`, payload, { headers });
        toast.success('Announcement updated');
      } else {
        await axios.post(`${API}/admin/announcements`, payload, { headers });
        toast.success('Announcement created');
      }
      resetForm();
      fetchItems();
      window.dispatchEvent(new CustomEvent('allure:announcement-changed'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save announcement');
    }
  };

  const toggleActive = async (row) => {
    try {
      await axios.patch(`${API}/admin/announcements/${row.id}`, { active: !row.active }, { headers });
      fetchItems();
      window.dispatchEvent(new CustomEvent('allure:announcement-changed'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to toggle');
    }
  };

  const remove = async (row) => {
    if (!window.confirm('Delete this announcement?')) return;
    try {
      await axios.delete(`${API}/admin/announcements/${row.id}`, { headers });
      fetchItems();
      window.dispatchEvent(new CustomEvent('allure:announcement-changed'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const renderVenueRow = (row) => (
    <>
      <Store size={14} color="#5B7EFF" />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#111' }}>{row.name}</span>
        <span style={{ fontSize: 11, color: '#777' }}>{row.address || row.city || ''}</span>
      </div>
    </>
  );

  const renderCityRow = (row) => (
    <>
      <MapPin size={14} color="#5B7EFF" />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#111' }}>{row.city}</span>
        <span style={{ fontSize: 11, color: '#777' }}>
          {row.country || '—'} · {row.timezone || 'tz unknown'}
        </span>
      </div>
    </>
  );

  return (
    <div data-testid="admin-announcements-panel" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Card style={{ padding: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <h2 style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 22 }}>System Announcements</h2>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#666' }}>Banner displayed at the top of the app to every signed-in user.</p>
          </div>
          {!showForm ? (
            <button onClick={() => { setShowForm(true); setEditingId(null); setForm(emptyForm); }} data-testid="admin-announcement-new-button"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 16px', borderRadius: 14, border: 'none', background: '#111', color: '#FFF', cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>
              <Plus size={14} /> New announcement
            </button>
          ) : null}
        </div>
      </Card>

      {showForm ? (
        <Card data-testid="admin-announcement-form" style={{ padding: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <p style={{ margin: 0, fontWeight: 700, fontFamily: "'Inria Serif', serif", fontSize: 16 }}>{editingId ? 'Edit announcement' : 'Create announcement'}</p>
            <button onClick={resetForm} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={16} /></button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Text
              <textarea value={form.text} onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))} data-testid="admin-announcement-text-input" rows={2} maxLength={280} placeholder="Up to 280 characters"
                style={{ display: 'block', width: '100%', marginTop: 6, padding: '10px 12px', border: '1px solid #E7E7E7', borderRadius: 12, fontSize: 14, resize: 'vertical' }} />
            </label>

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase' }}>
                Type
                <select value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))} data-testid="admin-announcement-type-select"
                  style={{ display: 'block', marginTop: 6, padding: '8px 12px', border: '1px solid #E7E7E7', borderRadius: 10, fontSize: 13 }}>
                  {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </label>
              <label style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase' }}>
                Audience (optional role filter)
                <select value={form.audience_role} onChange={(e) => setForm((f) => ({ ...f, audience_role: e.target.value }))} data-testid="admin-announcement-audience-select"
                  style={{ display: 'block', marginTop: 6, padding: '8px 12px', border: '1px solid #E7E7E7', borderRadius: 10, fontSize: 13 }}>
                  {ROLES.map((r) => <option key={r} value={r}>{r || 'All roles'}</option>)}
                </select>
              </label>
              <label style={{ fontSize: 12, color: '#444', display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 18 }}>
                <input type="checkbox" checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} data-testid="admin-announcement-active-checkbox" />
                Active
              </label>
              <label style={{ fontSize: 12, color: '#444', display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 18 }}>
                <input type="checkbox" checked={form.dismissible} onChange={(e) => setForm((f) => ({ ...f, dismissible: e.target.checked }))} data-testid="admin-announcement-dismissible-checkbox" />
                Dismissible
              </label>
            </div>

            {/* CTA picker */}
            <div style={{ borderTop: '1px dashed #E7E7E7', paddingTop: 14 }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 6px' }}>Call-to-action button</p>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {CTA_TARGETS.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, cta_target: t.id, cta_url: '', cta_venue: null }))}
                    data-testid={`admin-announcement-cta-target-${t.id}`}
                    style={{
                      padding: '6px 12px', borderRadius: 999, fontWeight: 700, fontSize: 12, cursor: 'pointer',
                      border: `1px solid ${form.cta_target === t.id ? '#111' : '#E7E7E7'}`,
                      background: form.cta_target === t.id ? '#111' : '#FFF',
                      color: form.cta_target === t.id ? '#FFF' : '#444',
                    }}
                  >{t.label}</button>
                ))}
              </div>

              {form.cta_target !== 'none' ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase' }}>
                    Button label
                    <input value={form.cta_label} onChange={(e) => setForm((f) => ({ ...f, cta_label: e.target.value }))} data-testid="admin-announcement-cta-label" placeholder='e.g. "See the venue"'
                      style={{ display: 'block', width: '100%', marginTop: 6, padding: '8px 12px', border: '1px solid #E7E7E7', borderRadius: 10, fontSize: 13 }} />
                  </label>
                  {form.cta_target === 'venue' ? (
                    form.cta_venue ? (
                      <div data-testid="admin-announcement-cta-venue-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 12, background: '#EEF2FF', border: '1px solid #C7D0FF', alignSelf: 'flex-start' }}>
                        <Store size={12} color="#5B7EFF" />
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#1F2945' }}>{form.cta_venue.name}</span>
                        {form.cta_venue.city ? <span style={{ fontSize: 11, color: '#5B7EFF' }}>· {form.cta_venue.city}</span> : null}
                        <button onClick={() => setForm((f) => ({ ...f, cta_venue: null }))} data-testid="admin-announcement-cta-venue-clear" style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={12} /></button>
                      </div>
                    ) : (
                      <AdminAutocomplete
                        endpoint="/admin/directory/venues"
                        headers={headers}
                        placeholder="Search a venue by name or city…"
                        renderRow={renderVenueRow}
                        onSelect={(row) => setForm((f) => ({ ...f, cta_venue: row }))}
                        emptyHint="No matching venues"
                        dataTestId="admin-announcement-venue-autocomplete"
                      />
                    )
                  ) : null}
                  {form.cta_target === 'route' ? (
                    <input value={form.cta_url} onChange={(e) => setForm((f) => ({ ...f, cta_url: e.target.value }))} data-testid="admin-announcement-cta-route" placeholder="/host/dashboard or /guest/venues" style={{ padding: '8px 12px', border: '1px solid #E7E7E7', borderRadius: 10, fontSize: 13 }} />
                  ) : null}
                  {form.cta_target === 'external' ? (
                    <input value={form.cta_url} onChange={(e) => setForm((f) => ({ ...f, cta_url: e.target.value }))} data-testid="admin-announcement-cta-external" placeholder="https://…" style={{ padding: '8px 12px', border: '1px solid #E7E7E7', borderRadius: 10, fontSize: 13 }} />
                  ) : null}
                  {computedCtaUrl ? (
                    <p style={{ margin: 0, fontSize: 11, color: '#666' }}><Globe size={10} style={{ verticalAlign: -1 }} /> Tapping the CTA goes to <code>{computedCtaUrl}</code></p>
                  ) : null}
                </div>
              ) : null}
            </div>

            {/* Schedule picker w/ timezone */}
            <div style={{ borderTop: '1px dashed #E7E7E7', paddingTop: 14 }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 6px' }}>Schedule</p>
              <p style={{ margin: 0, fontSize: 12, color: '#666' }}>
                <Calendar size={11} style={{ verticalAlign: -1, marginRight: 4 }} />
                Times entered below are interpreted in <strong>{form.schedule_city ? form.schedule_city.timezone || form.schedule_city.city : 'your browser timezone'}</strong> ({effectiveTimezone}). Pick a city if you want the announcement to align with that locale (useful for "Tonight in Athens"-style banners).
              </p>
              <div style={{ marginTop: 8 }}>
                {form.schedule_city ? (
                  <div data-testid="admin-announcement-tz-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 12px', borderRadius: 999, background: '#F4F5F8', border: '1px solid #E7E7E7' }}>
                    <MapPin size={12} color="#5B7EFF" />
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#111' }}>{form.schedule_city.city}</span>
                    <span style={{ fontSize: 11, color: '#777' }}>· {form.schedule_city.timezone || 'tz unknown'}</span>
                    <button onClick={() => setForm((f) => ({ ...f, schedule_city: null }))} data-testid="admin-announcement-tz-clear" style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={11} /></button>
                  </div>
                ) : (
                  <AdminAutocomplete
                    endpoint="/admin/directory/cities"
                    headers={headers}
                    placeholder="Optional · pick city for timezone (e.g. Athens)"
                    minChars={0}
                    renderRow={renderCityRow}
                    onSelect={(row) => setForm((f) => ({ ...f, schedule_city: row }))}
                    emptyHint="No matching cities"
                    dataTestId="admin-announcement-tz-autocomplete"
                  />
                )}
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', marginTop: 10, flexWrap: 'wrap' }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', flex: 1, minWidth: 200 }}>
                  Starts at (optional)
                  <input
                    type="datetime-local"
                    value={form.starts_at}
                    onChange={(e) => setForm((f) => ({ ...f, starts_at: e.target.value }))}
                    data-testid="admin-announcement-starts-at"
                    style={{ display: 'block', width: '100%', marginTop: 6, padding: '8px 12px', border: '1px solid #E7E7E7', borderRadius: 10, fontSize: 13 }}
                  />
                </label>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', flex: 1, minWidth: 200 }}>
                  Expires at (optional)
                  <input
                    type="datetime-local"
                    value={form.expires_at}
                    onChange={(e) => setForm((f) => ({ ...f, expires_at: e.target.value }))}
                    data-testid="admin-announcement-expires-at"
                    style={{ display: 'block', width: '100%', marginTop: 6, padding: '8px 12px', border: '1px solid #E7E7E7', borderRadius: 10, fontSize: 13 }}
                  />
                </label>
                {(form.starts_at || form.expires_at) ? (
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, starts_at: '', expires_at: '' }))}
                    data-testid="admin-announcement-schedule-clear"
                    style={{ padding: '9px 12px', borderRadius: 10, border: '1px solid #E7E7E7', background: '#FFF', cursor: 'pointer', fontSize: 12, color: '#666' }}
                  >
                    Clear schedule
                  </button>
                ) : null}
              </div>
              <p style={{ margin: '8px 0 0', fontSize: 11, color: '#999' }}>
                Schedule is optional. Leave blank to publish immediately and keep showing until you pause it.
              </p>
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
              <button onClick={save} data-testid="admin-announcement-save-button"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 16px', borderRadius: 12, border: 'none', background: '#111', color: '#FFF', cursor: 'pointer', fontWeight: 700 }}>
                <Save size={14} /> {editingId ? 'Save changes' : 'Publish'}
              </button>
              <button onClick={resetForm} style={{ padding: '10px 16px', borderRadius: 12, border: '1px solid #E7E7E7', background: '#FFF', cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        </Card>
      ) : null}

      <div data-testid="admin-announcements-list" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {loading ? <p style={{ textAlign: 'center', color: '#999' }}>Loading…</p> : null}
        {!loading && items.length === 0 ? (
          <EmptyState
            icon={Megaphone}
            tone="info"
            title="No announcements yet"
            body="Use announcements to ship a banner across the whole app — a promo, a heads-up, or a critical alert. You can deep-link the CTA straight to a venue or in-app route."
            actionLabel="New announcement"
            onAction={() => { setShowForm(true); setEditingId(null); setForm(emptyForm); }}
            dataTestId="admin-announcements-empty-state"
          />
        ) : null}
        {items.map((row) => {
          const nowIso = new Date().toISOString();
          const queued = row.active && row.starts_at && row.starts_at > nowIso;
          const expired = row.active && row.expires_at && row.expires_at <= nowIso;
          const liveNow = row.active && !queued && !expired;
          const fmtSchedule = (iso) => {
            if (!iso) return '';
            const d = new Date(iso);
            return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
          };
          return (
          <Card key={row.id} data-testid={`admin-announcement-row-${row.id}`} style={{ padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 220 }}>
                <p style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 14, fontWeight: 700 }}>
                  <Megaphone size={14} style={{ marginRight: 6, verticalAlign: -2 }} /> {row.text}
                </p>
                <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                  <Badge text={row.type} color={TYPE_BADGE[row.type]} />
                  {liveNow ? <Badge text="live now" color="green" /> : null}
                  {queued ? <Badge text={`scheduled · ${fmtSchedule(row.starts_at)}`} color="blue" /> : null}
                  {expired ? <Badge text={`expired · ${fmtSchedule(row.expires_at)}`} color="" /> : null}
                  {!row.active ? <Badge text="paused" color="" /> : null}
                  {row.audience_role ? <Badge text={`role: ${row.audience_role}`} color="blue" /> : <Badge text="all roles" color="" />}
                  {row.dismissible ? null : <Badge text="non-dismissible" color="orange" />}
                  {row.cta_url ? <Badge text={`cta → ${row.cta_url.length > 28 ? row.cta_url.slice(0, 28) + '…' : row.cta_url}`} color="blue" /> : null}
                </div>
                {(row.starts_at || row.expires_at) ? (
                  <p style={{ margin: '6px 0 0', fontSize: 11, color: '#888' }}>
                    {row.starts_at ? `Starts ${fmtSchedule(row.starts_at)}` : 'Started immediately'}
                    {' · '}
                    {row.expires_at ? `expires ${fmtSchedule(row.expires_at)}` : 'no expiry'}
                  </p>
                ) : null}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => toggleActive(row)} data-testid={`admin-announcement-toggle-${row.id}`} style={{ padding: '6px 10px', borderRadius: 10, border: '1px solid #E7E7E7', background: '#FFF', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}><Power size={12} /> {row.active ? 'Pause' : 'Activate'}</button>
                <button onClick={() => startEdit(row)} data-testid={`admin-announcement-edit-${row.id}`} style={{ padding: '6px 10px', borderRadius: 10, border: '1px solid #E7E7E7', background: '#FFF', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}><Edit3 size={12} /> Edit</button>
                <button onClick={() => remove(row)} data-testid={`admin-announcement-delete-${row.id}`} style={{ padding: '6px 10px', borderRadius: 10, border: '1px solid #DD0608', background: '#FFF', color: '#DD0608', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}><Trash2 size={12} /></button>
              </div>
            </div>
          </Card>
          );
        })}
      </div>
    </div>
  );
};

export default AdminAnnouncementsPanel;
